import { useState } from 'react';
import { useWorkbook } from '@/context/WorkbookContext';
import { 
  WorkbookPage, 
  RuleHeader, 
  SectionTitle, 
  Paragraph, 
  Callout,
  ExampleBox,
  StepList,
  WorksheetInput,
  CheckboxItem,
  AffirmationBox,
  RecapBox,
  OptionalSection,
  AutoTotal,
  parseMoney
} from '@/components/WorkbookPage';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const FREQ_MULTIPLIER = {
  weekly: 4.33,
  biweekly: 2.17,
  semimonthly: 2,
  monthly: 1,
  irregular: 4.33,
};

const FREQ_INCOME_LABEL = {
  weekly: 'What is your typical weekly income? (after taxes)',
  biweekly: 'What is your typical biweekly paycheck? (after taxes)',
  semimonthly: 'What is your typical paycheck? (twice a month, after taxes)',
  monthly: 'What is your monthly income? (after taxes)',
  irregular: 'What is your average weekly income? (after taxes)',
  '': 'What is your typical paycheck? (after taxes)',
};

const FREQ_TOTAL_LABEL = {
  weekly: 'Monthly income estimate (weekly × 4.33)',
  biweekly: 'Monthly income estimate (biweekly × 2.17)',
  semimonthly: 'Monthly income estimate (twice a month × 2)',
  monthly: 'Monthly income estimate',
  irregular: 'Monthly income estimate (avg weekly × 4.33)',
  '': 'Monthly income estimate',
};

export const Rule1Page = () => {
  const { formData, updateFormData } = useWorkbook();
  const data = formData.rule1 || {};
  
  const updateData = (field, value) => {
    updateFormData('rule1', { ...data, [field]: value });
  };

  const freq = data.incomeFrequency || '';
  const multiplier = FREQ_MULTIPLIER[freq] || 4.33;
  const monthlyEstimate = parseMoney(data.weeklyIncome) * multiplier;
  
  return (
    <WorkbookPage pageId="rule-1" prevPage="section-1-divider" nextPage="rule-2">
      <RuleHeader ruleNumber="1" title="Know Your Numbers" section="stability" />
      
      {/* Overview */}
      <SectionTitle>What This Rule Means</SectionTitle>
      <Paragraph>
        Before you can manage your money well, you need to know exactly how much is coming in. 
        This sounds simple, but many people don't have a clear picture of their true income—especially 
        if they work irregular hours, have multiple jobs, or get paid at different times.
      </Paragraph>
      <Paragraph>
        <strong>Your income is the foundation.</strong> Everything else in this workbook builds on it. 
        When you know your numbers, you can plan. When you don't, you're just guessing.
      </Paragraph>
      
      {/* Why It Matters */}
      <SectionTitle>Why It Matters</SectionTitle>
      <Paragraph>
        Imagine trying to build a house without measuring the land first. You might build something 
        too big, too small, or in the wrong spot. The same is true with money. If you don't know 
        what you're working with, your plans won't fit your reality.
      </Paragraph>
      <Callout type="info">
        <strong>Real talk:</strong> Many people avoid looking at their numbers because they're scared 
        of what they'll see. That's normal. But knowing the truth—even if it's hard—gives you power. 
        You can't fix what you don't face.
      </Callout>
      
      {/* Examples */}
      <SectionTitle>Real-Life Examples</SectionTitle>
      
      <ExampleBox title="Weekly Pay">
        <p><strong>Marcus</strong> works at a warehouse and gets paid every Friday. He earns $15/hour and 
        usually works 38 hours per week.</p>
        <p className="mt-2">Weekly income: $15 × 38 = <strong>$570/week</strong></p>
        <p>Monthly estimate: $570 × 4.33 = <strong>$2,468/month</strong></p>
      </ExampleBox>
      
      <ExampleBox title="Biweekly Pay">
        <p><strong>Tasha</strong> is a medical assistant paid every two weeks. Her paycheck is $1,450 
        after taxes.</p>
        <p className="mt-2">Biweekly income: <strong>$1,450</strong></p>
        <p>Monthly estimate: $1,450 × 2.17 = <strong>$3,147/month</strong></p>
        <p className="text-xs text-muted-foreground mt-2">(We use 2.17 because there are 26 pay periods per year ÷ 12 months)</p>
      </ExampleBox>
      
      <ExampleBox title="Irregular Income">
        <p><strong>DeShawn</strong> does gig work—delivery apps, odd jobs, and freelance. His income 
        varies every week from $200 to $800.</p>
        <p className="mt-2">He tracks the last 8 weeks and adds them up:</p>
        <p>$450 + $320 + $600 + $280 + $550 + $400 + $720 + $380 = $3,700</p>
        <p>Average weekly income: $3,700 ÷ 8 = <strong>$462/week</strong></p>
        <p>Monthly estimate: $462 × 4.33 = <strong>$2,000/month</strong></p>
      </ExampleBox>
      
      <ExampleBox title="Fixed Income (Monthly)">
        <p><strong>Mrs. Johnson</strong> is retired and receives Social Security on the 3rd of each month: 
        <strong>$1,890</strong>. That's her only income, so planning is straightforward—but tight.</p>
      </ExampleBox>
      
      {/* Step by Step */}
      <SectionTitle>Step-by-Step Instructions</SectionTitle>
      <StepList steps={[
        "Gather your pay stubs, bank statements, or payment app history for the last 4-8 weeks.",
        "Write down every source of income (job, side work, benefits, child support, etc.).",
        "Add up your total income for the period you're tracking.",
        "Divide by the number of weeks to get your weekly average.",
        "Multiply your weekly average by 4.33 to estimate your monthly income.",
        "Use the LOWER estimate if your income varies—it's safer to plan conservatively."
      ]} />
      
      {/* Worksheet */}
      <div className="bg-muted/30 rounded-xl p-6 my-8 border border-border">
        <h3 className="font-serif font-bold text-lg mb-6">📝 Your Income Worksheet</h3>
        
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium mb-2">How often do you get paid?</label>
            <Select 
              value={data.incomeFrequency || ''} 
              onValueChange={(v) => updateData('incomeFrequency', v)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select your pay frequency" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="weekly">Weekly (every week)</SelectItem>
                <SelectItem value="biweekly">Biweekly (every two weeks)</SelectItem>
                <SelectItem value="semimonthly">Semi-monthly (twice a month)</SelectItem>
                <SelectItem value="monthly">Monthly (once a month)</SelectItem>
                <SelectItem value="irregular">Irregular / Varies</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <WorksheetInput
            label={FREQ_INCOME_LABEL[freq]}
            placeholder="$___"
            value={data.weeklyIncome || ''}
            onChange={(v) => updateFormData('rule1', { ...data, weeklyIncome: v, totalMonthlyIncome: (parseMoney(v) * multiplier).toFixed(2) })}
          />
          
          <AutoTotal
            label={FREQ_TOTAL_LABEL[freq]}
            value={monthlyEstimate}
            hint="Calculated automatically from your income above"
            testId="rule1-monthly-total"
          />
          
          <WorksheetInput
            label="List all your income sources:"
            placeholder="Job 1, Side gig, Child support, etc."
            value={data.incomeNotes || ''}
            onChange={(v) => updateData('incomeNotes', v)}
            multiline
          />
        </div>
      </div>
      
      {/* Checklist */}
      <div className="my-8">
        <h3 className="font-semibold mb-4">Before moving on, confirm:</h3>
        <CheckboxItem 
          checked={data.checklist1 || false}
          onChange={(v) => updateData('checklist1', v)}
        >
          I know my pay frequency (weekly, biweekly, monthly, or irregular)
        </CheckboxItem>
        <CheckboxItem 
          checked={data.checklist2 || false}
          onChange={(v) => updateData('checklist2', v)}
        >
          I've calculated my average weekly income
        </CheckboxItem>
        <CheckboxItem 
          checked={data.checklist3 || false}
          onChange={(v) => updateData('checklist3', v)}
        >
          I've estimated my monthly income
        </CheckboxItem>
        <CheckboxItem 
          checked={data.checklist4 || false}
          onChange={(v) => updateData('checklist4', v)}
        >
          I've listed all my income sources
        </CheckboxItem>
      </div>
      
      {/* Recap */}
      <RecapBox items={[
        "Your income is the foundation—know it before planning anything else",
        "Track at least 4-8 weeks to find your average",
        "Weekly × 4.33 = monthly estimate",
        "If income varies, plan using the lower estimate"
      ]} />
      
      {/* Affirmation */}
      <AffirmationBox>
        I am capable of understanding my finances. Knowing my numbers gives me power to make good decisions.
      </AffirmationBox>
      
      {/* Optional Reflection */}
      <OptionalSection>
        <p className="mb-4">Take a moment to reflect:</p>
        <ul className="list-disc list-inside space-y-2 text-sm">
          <li>How did it feel to write down your real numbers?</li>
          <li>What stories have you told yourself about money that might not be true?</li>
          <li>Is there a sense of relief in facing the truth, even if it's hard?</li>
        </ul>
        <WorksheetInput
          label="Your thoughts:"
          placeholder="Write anything that comes to mind..."
          value={data.reflection || ''}
          onChange={(v) => updateData('reflection', v)}
          multiline
        />
      </OptionalSection>
    </WorkbookPage>
  );
};
